// frontend/src/components/MlPredict/MlPredictForm.tsx

import React, { useMemo, useState } from 'react';
import type { ModelSchema } from '../../services/mlModels.service';

interface Props {
  schema: ModelSchema;
  predicting: boolean;
  onSubmit: (values: Record<string, number>) => void;
}

/**
 * Analisa a lista de features e agrupa:
 *   - NumericFeatures: features "soltas" (ex.: "age", "tenure_months")
 *   - CategoricalGroups: grupos por prefixo (ex.: "gender" → ["gender_Male", "gender_Female"])
 */
function analisarFeatures(features: string[]) {
  const grupos = new Map<string, string[]>();

  for (const f of features) {
    const underscoreIndex = f.indexOf('_');
    const prefixo = underscoreIndex >= 0 ? f.slice(0, underscoreIndex) : f;

    if (!grupos.has(prefixo)) {
      grupos.set(prefixo, []);
    }
    grupos.get(prefixo)!.push(f);
  }

  const numericFeatures: string[] = [];
  const categoricalGroups: { grupo: string; opcoes: string[] }[] = [];

  for (const [prefixo, cols] of grupos) {
    if (cols.length === 1) {
      numericFeatures.push(cols[0]);
    } else {
      categoricalGroups.push({ grupo: prefixo, opcoes: cols.sort() });
    }
  }

  return { numericFeatures, categoricalGroups };
}

export const MlPredictForm: React.FC<Props> = ({
  schema,
  predicting,
  onSubmit,
}) => {
  const { numericFeatures, categoricalGroups } = useMemo(
    () => analisarFeatures(schema.feature_schema.features),
    [schema]
  );

  // estado: para cada feature, o valor
  const [values, setValues] = useState<Record<string, number>>(() => {
    const init: Record<string, number> = {};
    for (const f of numericFeatures) init[f] = 0;
    for (const g of categoricalGroups) {
      for (const opt of g.opcoes) init[opt] = 0;
    }
    return init;
  });

  const setNumericValue = (feature: string, value: number) => {
    setValues((prev) => ({ ...prev, [feature]: value }));
  };

  const setCategoricalValue = (opcoes: string[], escolhida: string) => {
    setValues((prev) => {
      const next = { ...prev };
      for (const opt of opcoes) {
        next[opt] = opt === escolhida ? 1 : 0;
      }
      return next;
    });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(values);
  };

  return (
    <form className="predict-form" onSubmit={handleSubmit}>
      <h3>📝 Dados de Entrada</h3>

      {numericFeatures.length > 0 && (
        <div className="form-block">
          <h4>Valores Numéricos</h4>
          <div className="form-grid">
            {numericFeatures.map((f) => (
              <label key={f} className="form-field">
                <span className="field-label">{f}</span>
                <input
                  type="number"
                  step="any"
                  value={values[f] ?? 0}
                  onChange={(e) => setNumericValue(f, parseFloat(e.target.value) || 0)}
                />
              </label>
            ))}
          </div>
        </div>
      )}

      {categoricalGroups.length > 0 && (
        <div className="form-block">
          <h4>Escolhas Categóricas</h4>
          {categoricalGroups.map((g) => (
            <div key={g.grupo} className="form-group">
              <span className="group-label">{g.grupo}</span>
              <div className="group-options">
                {g.opcoes.map((opt) => {
                  const ativo = values[opt] === 1;
                  return (
                    <button
                      key={opt}
                      type="button"
                      className={`group-option ${ativo ? 'active' : ''}`}
                      onClick={() => setCategoricalValue(g.opcoes, opt)}
                    >
                      {opt.replace(`${g.grupo}_`, '')}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="form-actions">
        <button
          type="submit"
          className="btn-predict"
          disabled={predicting}
        >
          {predicting ? '⏳ Predizendo...' : '🎯 Predizer'}
        </button>
      </div>
    </form>
  );
};